import { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { Badge } from "../../../../components/ui/badge";
import { Separator } from "../../../../components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../components/ui/tooltip";
import {
  Cloud,
  ArrowLeft,
  CheckCircle,
  Plus,
  X,
  Search,
  Trash2,
  Settings,
  Upload,
  FileJson,
  Loader2
} from "lucide-react";
import { Link } from "wouter";
import { useToast } from "../../../../hooks/use-toast";
import { GCPProject, GCPSetupData, SettingProps } from "../interface";
import { myLocalStorage } from "../../../StorageHelper";
import { gcpConfig, gcpSaveConfig, gcpSyncStatus, gcpUnConfig } from "../Api/settingsApi";
import { FaGoogle } from "react-icons/fa6";
import useCloudSyncStore from "../../../../services/useCloudSyncStore";


const MASKED_VALUE = "**********";

export default function GCPSettings({ onBack }: SettingProps) {
  const [gcpData, setGcpData] = useState<GCPSetupData>();
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>("1");
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    clientId: "",
    clientEmail: "",
    privateKey: "",
    privateKeyId: "",
    projectId: "",
    credentialsType: "service_account"
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasInProgressSync, setHasInProgressSync] = useState(false);
  const [isSyncStatusLoaded, setIsSyncStatusLoaded] = useState(false);
  const isStoreSyncing = useCloudSyncStore((state) => Boolean(state.syncStatus.gcp));
  const isGcpSyncing = isStoreSyncing || hasInProgressSync;
  const isDeleteUnavailable = !isSyncStatusLoaded || isGcpSyncing;

  const { toast } = useToast();

  const filteredProjects = gcpData?.projects.filter(project =>
    project.clientEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedProject = gcpData?.projects.find(p => p.id === selectedProjectId);

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddProject = async () => {
    if (!formData.clientId || !formData.clientEmail || !formData.privateKey || !formData.projectId) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      const privateString = formData.privateKey.replace(/\\n/g, "\n");
      const payload = {
        client_id: formData.clientId,
        client_email: formData.clientEmail,
        private_key: privateString,
        private_key_id: formData.privateKeyId,
        project_id: formData.projectId,
        tenantName: tenantName,
        type: formData.credentialsType,
      };
      const jsonString = JSON.stringify(payload, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const formDataWithJson = new FormData();
      formDataWithJson.append("file", blob, "form-data.json");

      await gcpSaveConfig(tenantName, formDataWithJson)

      const newProject: GCPProject = {
        id: Date.now().toString(),
        projectId: formData.projectId,
        clientEmail: formData.clientEmail,
        privateKey: MASKED_VALUE,
        privateKeyId: MASKED_VALUE,
        credentialsType: MASKED_VALUE
      };

      setGcpData((prev) => {
        if (!prev) {
          return {
            projects: [newProject],
            isConfigured: true,
          };
        }
        return {
          ...prev,
          projects: [...prev.projects, newProject],
          isConfigured: true,
        };
      });


      // Reset form
      setFormData({
        clientId: "",
        clientEmail: "",
        privateKey: "",
        privateKeyId: "",
        projectId: "",
        credentialsType: "service_account"
      });

      setShowAddForm(false);
      setSelectedProjectId(newProject.id);

      toast({
        title: "Project Added",
        description: "GCP project has been configured successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to configure GCP project. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveProject = async (projectIdentifier: string) => {
    if (isDeleteUnavailable) return;
    setDeletingProjectId(projectIdentifier);
    try {
      await gcpUnConfig(tenantName, projectIdentifier);

      await fetchTheConfiguration();

      if (selectedProject?.clientEmail === projectIdentifier) {
        setSelectedProjectId(null);
      }

      toast({
        title: "Project Removed",
        description: "GCP project has been removed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove project. Please try again.",
        variant: "destructive"
      });
    } finally {
      setDeletingProjectId(null);
    }
  };


  const parseGCPJSON = (jsonContent: string) => {
    try {
      const gcpConfig = JSON.parse(jsonContent);

      // Map GCP JSON fields to our form fields
      setFormData({
        clientId: gcpConfig.client_id || "",
        clientEmail: gcpConfig.client_email || "",
        privateKey: gcpConfig.private_key || "",
        privateKeyId: gcpConfig.private_key_id || "",
        projectId: gcpConfig.project_id || "",
        credentialsType: gcpConfig.type || "service_account"
      });

      toast({
        title: "JSON Loaded",
        description: "GCP configuration loaded from JSON file successfully.",
      });
    } catch (error) {
      toast({
        title: "Invalid JSON",
        description: "Failed to parse GCP JSON file. Please check the file format.",
        variant: "destructive"
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: "Invalid File",
        description: "Please upload a JSON file.",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      parseGCPJSON(content);
    };
    reader.readAsText(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast({
        title: "Invalid File",
        description: "Please upload a JSON file.",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      parseGCPJSON(content);
    };
    reader.readAsText(file);
  };

  const fetchTheConfiguration = async () => {
    let response = await gcpConfig(tenantName);

    const projects: GCPProject[] = response.map((item: any, index: number) => ({
      id: item.clientEmail || String(index),
      projectId: MASKED_VALUE,
      clientEmail: item.clientEmail,
      privateKey: MASKED_VALUE,
      privateKeyId: MASKED_VALUE,
      credentialsType: MASKED_VALUE,
    }));

    const gcpData: GCPSetupData = {
      projects,
      isConfigured: projects.length > 0,
    };
    if (projects.length > 0)
      setSelectedProjectId(projects[0].id)
    setGcpData(gcpData)
  }


  useEffect(() => {
    fetchTheConfiguration();
  }, [tenantName])

  useEffect(() => {
    let isActive = true;
    setIsSyncStatusLoaded(false);

    const fetchStatus = async () => {
      try {
        const statuses = await gcpSyncStatus(tenantName);
        if (isActive) {
          setHasInProgressSync(statuses.some((status: any) => status.syncStatus === "IN_PROGRESS"));
          setIsSyncStatusLoaded(true);
        }
      } catch {
        return;
      }
    };
    fetchStatus();
    const interval = window.setInterval(fetchStatus, 5000);
    return () => {
      isActive = false;
      window.clearInterval(interval);
    };
  }, [tenantName]);

  const canAddProject = formData.clientId && formData.clientEmail && formData.privateKey && formData.projectId;

  return (
    <div className="tenant-settings-page flex-1 space-y-6 p-6">
      {/* Header */}
      <Card className="settings-header-card border-border/40 rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="settings-back-button"
              onClick={onBack}
              aria-label="Back to settings"
              title="Back to settings"
            >

              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
              <FaGoogle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                    GCP Setup
                  </h1>
                  <p className="text-muted-foreground">
                    Configure Google Cloud Platform integration
                  </p>
                </div>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="gap-2"
                  data-testid="button-add-configuration"
                >
                  <Plus className="w-4 h-4" />
                  Add Configuration
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Status */}
      {gcpData?.isConfigured && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950 p-3 rounded-2xl border border-green-200 dark:border-green-800 shadow-sm">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">GCP account configured successfully</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Panel - Project List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search Projects */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search Project"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="input-search-projects"
            />
          </div>

          {/* Project List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredProjects?.map((project) => (
              <Card
                key={project.id}
                className={`cursor-pointer transition-colors hover-elevate rounded-2xl ${selectedProjectId === project.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                  : 'border-border/40'
                  }`}
                onClick={() => {
                  setSelectedProjectId(project.id);
                  setShowAddForm(false);
                }}
                data-testid={`card-project-${project.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="font-medium text-sm">
                        Project ID: <span className="text-blue-600 dark:text-blue-400">{MASKED_VALUE}</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Client Email: {project.clientEmail}
                      </p>
                    </div>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveProject(project.clientEmail)}
                              disabled={isDeleteUnavailable || deletingProjectId === project.clientEmail}
                              className="h-6 w-6 p-0 text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                              data-testid={`button-remove-project-${project.id}`}
                            >
                              {deletingProjectId === project.clientEmail ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {isDeleteUnavailable && (
                          <TooltipContent side="top">
                            <p>
                              {isSyncStatusLoaded
                                ? "Delete configuration unavailable."
                                : "Loading sync status..."}
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </CardContent>
              </Card>
            ))}

            {filteredProjects?.length === 0 && gcpData?.projects.length !== 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No projects found matching your search</p>
              </div>
            )}

            {gcpData?.projects.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Cloud className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No GCP projects configured</p>
                <p className="text-xs">Click "Add Configuration" to get started</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Project Details or Add Form */}
        <div className="lg:col-span-3">
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle>
                {showAddForm ? "Add New Project" : selectedProject ? "Project Details" : "Project Details"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {showAddForm ? (
                /* Add Project Form */
                <>
                  <div className="space-y-2">
                    <Label htmlFor="client-id">Client ID</Label>
                    <Input
                      id="client-id"
                      value={formData.clientId}
                      onChange={(e) => handleFormChange("clientId", e.target.value)}
                      placeholder="Enter Client ID"
                      data-testid="input-client-id"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="client-email">Client Email</Label>
                    <Input
                      id="client-email"
                      value={formData.clientEmail}
                      onChange={(e) => handleFormChange("clientEmail", e.target.value)}
                      placeholder="service-account-credentials@project.iam.gserviceaccount.com"
                      data-testid="input-client-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="private-key">Private Key</Label>
                    <Textarea
                      id="private-key"
                      value={formData.privateKey}
                      onChange={(e) => handleFormChange("privateKey", e.target.value)}
                      placeholder="-----BEGIN PRIVATE KEY-----"
                      rows={4}
                      data-testid="textarea-private-key"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="private-key-id">Private Key ID</Label>
                    <Input
                      id="private-key-id"
                      value={formData.privateKeyId}
                      onChange={(e) => handleFormChange("privateKeyId", e.target.value)}
                      placeholder="Enter Private Key ID"
                      data-testid="input-private-key-id"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="project-id">Project ID</Label>
                    <Input
                      id="project-id"
                      value={formData.projectId}
                      onChange={(e) => handleFormChange("projectId", e.target.value)}
                      placeholder="Enter GCP Project ID"
                      data-testid="input-project-id"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="credentials-type">Credentials Type</Label>
                    <Input
                      id="credentials-type"
                      value={formData.credentialsType}
                      onChange={(e) => handleFormChange("credentialsType", e.target.value)}
                      placeholder="service_account"
                      data-testid="input-credentials-type"
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={handleAddProject}
                      disabled={!canAddProject || isLoading}
                      className="flex-1"
                      data-testid="button-save-project"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Configuration"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowAddForm(false)}
                      data-testid="button-cancel-add"
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="relative py-4">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-card px-4 text-muted-foreground font-medium">OR</span>
                    </div>
                  </div>

                  {/* Hidden File Input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="input-file-json"
                  />

                  {/* Drag & Drop Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`settings-dropzone border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${isDragging
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                      : 'border-muted hover:border-blue-400 hover:bg-muted/50'
                      }`}
                    data-testid="dropzone-gcp-json"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${isDragging
                        ? 'bg-blue-100 dark:bg-blue-900/40'
                        : 'bg-muted'
                        }`}>
                        {isDragging ? (
                          <Upload className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <FileJson className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {isDragging ? 'Drop your file here' : 'Drag & Drop your GCP JSON file here'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          or click to select
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : selectedProject ? (
                /* Selected Project Details */
                <>
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input
                      value={MASKED_VALUE}
                      readOnly
                      className="bg-muted/30"
                      data-testid="display-client-id"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Client Email</Label>
                    <Input
                      value={selectedProject.clientEmail}
                      readOnly
                      className="bg-muted/30"
                      data-testid="display-client-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Private Key</Label>
                    <Input
                      value={MASKED_VALUE}
                      readOnly
                      className="bg-muted/30"
                      data-testid="display-private-key"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Private Key ID</Label>
                    <Input
                      value={MASKED_VALUE}
                      readOnly
                      className="bg-muted/30"
                      data-testid="display-private-key-id"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Project ID</Label>
                    <Input
                      value={MASKED_VALUE}
                      readOnly
                      className="bg-muted/30"
                      data-testid="display-project-id"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Credentials Type</Label>
                    <Input
                      value={MASKED_VALUE}
                      readOnly
                      className="bg-muted/30"
                      data-testid="display-credentials-type"
                    />
                  </div>
                </>
              ) : (
                /* No Project Selected */
                <div className="text-center py-8 text-muted-foreground">
                  <Settings className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select a project to view details</p>
                  <p className="text-xs">or add a new configuration</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
